import { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const NAV_GROUPS = [
  {
    label: null, // no group label for top item
    items: [
      { to: '/dashboard', icon: '⬛', label: 'Dashboard' },
    ]
  },
  {
    label: 'Properties',
    items: [
      { to: '/properties', icon: '🏘', label: 'Properties' },
      { to: '/deposits',   icon: '💰', label: 'Deposits' },
      { to: '/documents',  icon: '📬', label: 'Documents' },
      { to: '/notes',      icon: '📝', label: 'Notes' },
    ]
  },
  {
    label: 'Compliance',
    items: [
      { to: '/tasks',   icon: '✅', label: 'Tasks' },
      { to: '/rra',     icon: '📋', label: 'RRA 2025' },
      { to: '/hmo',     icon: '🏠', label: 'HMO' },
    ]
  },
  {
    label: 'Finance',
    items: [
      { to: '/rent', icon: '💷', label: 'Rent Tracker' },
    ]
  },
  {
    label: 'Planning',
    items: [
      { to: '/calendar',  icon: '📅', label: 'Calendar' },
      { to: '/reminders', icon: '🔔', label: 'Reminders' },
    ]
  },
  {
    label: 'Account',
    items: [
      { to: '/billing', icon: '💳', label: 'Billing' },
      { to: '/profile', icon: '👤', label: 'Profile' },
      { to: '/contact', icon: '✉️', label: 'Contact Us' },
    ]
  },
];

// Flat list for mobile bottom nav
const NAV = NAV_GROUPS.flatMap(g => g.items);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  function handleLogout() { logout(); navigate('/login'); }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>

      {/* ── Desktop sidebar ── */}
      <aside style={{
        width: 220, background: 'var(--bg-dark)', color: '#fff',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        padding: '28px 0', position: 'sticky', top: 0, height: '100vh',
      }} className="desktop-sidebar">
        <div style={{ padding: '0 24px 28px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
          <p style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 300, lineHeight: 1.2 }}>
            Landlord<br /><em>Comply</em>
          </p>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', marginTop: 4 }}>UK Compliance Hub</p>
        </div>
        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi} style={{ marginBottom: 8 }}>
              {group.label && (
                <p style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.25)', textTransform: 'uppercase', letterSpacing: '.08em', padding: '8px 12px 4px' }}>
                  {group.label}
                </p>
              )}
              {group.items.map(({ to, icon, label }) => (
                <NavLink key={to} to={to} style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 8, marginBottom: 1,
                  fontSize: 13, fontWeight: isActive ? 500 : 400,
                  color: isActive ? '#fff' : 'rgba(255,255,255,.5)',
                  background: isActive ? 'rgba(255,255,255,.1)' : 'transparent',
                  transition: 'all .15s', textDecoration: 'none',
                })}>
                  <span style={{ fontSize: 15 }}>{icon}</span>{label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,.08)' }}>
          <p style={{ fontSize: 13, fontWeight: 500, color: '#fff', marginBottom: 2 }}>{user?.full_name}</p>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', marginBottom: 10 }}>{user?.email}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <button onClick={handleLogout} style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Sign out →</button>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <Link to="/privacy" style={{ fontSize: 10, color: 'rgba(255,255,255,.2)', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/terms"   style={{ fontSize: 10, color: 'rgba(255,255,255,.2)', textDecoration: 'none' }}>Terms</Link>
          </div>
        </div>
      </aside>

      {/* ── Mobile overlay menu ── */}
      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 50 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 260, height: '100%', background: 'var(--bg-dark)', display: 'flex', flexDirection: 'column', padding: '24px 0' }}>
            <div style={{ padding: '0 20px 20px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
              <p style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 300, color: '#fff', lineHeight: 1.2 }}>
                Landlord<br /><em>Comply</em>
              </p>
            </div>
            <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
              {NAV_GROUPS.map((group, gi) => (
                <div key={gi} style={{ marginBottom: 8 }}>
                  {group.label && (
                    <p style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.25)', textTransform: 'uppercase', letterSpacing: '.08em', padding: '8px 12px 4px' }}>
                      {group.label}
                    </p>
                  )}
                  {group.items.map(({ to, icon, label }) => (
                    <NavLink key={to} to={to} onClick={() => setMenuOpen(false)} style={({ isActive }) => ({
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '11px 12px', borderRadius: 8, marginBottom: 1,
                      fontSize: 14, fontWeight: isActive ? 500 : 400,
                      color: isActive ? '#fff' : 'rgba(255,255,255,.6)',
                      background: isActive ? 'rgba(255,255,255,.1)' : 'transparent',
                      textDecoration: 'none',
                    })}>
                      <span style={{ fontSize: 17 }}>{icon}</span>{label}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
            <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,.08)' }}>
              <p style={{ fontSize: 13, color: '#fff', marginBottom: 4 }}>{user?.email}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <NavLink to="/profile" onClick={() => setMenuOpen(false)} style={{ fontSize: 13, color: 'rgba(255,255,255,.5)', textDecoration: 'none' }}>⚙️ Profile</NavLink>
                <button onClick={handleLogout} style={{ fontSize: 13, color: 'rgba(255,255,255,.5)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Sign out</button>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <Link to="/privacy" style={{ fontSize: 11, color: 'rgba(255,255,255,.2)', textDecoration: 'none' }}>Privacy</Link>
                <Link to="/terms"   style={{ fontSize: 11, color: 'rgba(255,255,255,.2)', textDecoration: 'none' }}>Terms</Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Mobile top bar */}
        <header style={{
          display: 'none',
          background: 'var(--bg-dark)', color: '#fff',
          padding: '14px 20px', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 40,
        }} className="mobile-header">
          <p style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 300 }}>
            Landlord<em>Comply</em>
          </p>
          <button onClick={() => setMenuOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <div style={{ width: 22, height: 2, background: '#fff', marginBottom: 5, borderRadius: 1 }} />
            <div style={{ width: 22, height: 2, background: '#fff', marginBottom: 5, borderRadius: 1 }} />
            <div style={{ width: 22, height: 2, background: '#fff', borderRadius: 1 }} />
          </button>
        </header>

        <main style={{ flex: 1, padding: '40px 48px' }} className="main-content">
          {children}
        </main>

        {/* Mobile bottom nav — show key pages only */}
        <nav style={{
          display: 'none',
          background: 'var(--bg-dark)',
          borderTop: '1px solid rgba(255,255,255,.1)',
          padding: '8px 0 env(safe-area-inset-bottom)',
          position: 'sticky', bottom: 0, zIndex: 40,
        }} className="mobile-bottom-nav">
          {NAV.slice(0, 5).map(({ to, icon, label }) => (
            <NavLink key={to} to={to} style={({ isActive }) => ({
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              padding: '6px 0', flex: 1,
              color: isActive ? '#fff' : 'rgba(255,255,255,.45)',
              fontSize: 10, fontWeight: isActive ? 600 : 400,
            })}>
              <span style={{ fontSize: 20 }}>{icon}</span>
              {label.split(' ')[0]}
            </NavLink>
          ))}
        </nav>
      </div>

      <style>{`
        @media (max-width: 767px) {
          .desktop-sidebar { display: none !important; }
          .mobile-header { display: flex !important; }
          .mobile-bottom-nav { display: flex !important; }
          .main-content { padding: 16px 12px 80px !important; }

          /* Force all inline grids to single column on mobile */
          .main-content [style*="gridTemplateColumns: '240px"] { grid-template-columns: 1fr !important; }
          .main-content [style*="gridTemplateColumns: '1fr 1fr"] { grid-template-columns: 1fr !important; }
          .main-content [style*="gridTemplateColumns: '2fr 1fr"] { grid-template-columns: 1fr !important; }
          .main-content [style*="gridTemplateColumns: '1fr 2fr"] { grid-template-columns: 1fr !important; }
          .main-content [style*="gridTemplateColumns: '3fr 1fr"] { grid-template-columns: 1fr !important; }
          .main-content [style*="gridTemplateColumns: '1fr 3fr"] { grid-template-columns: 1fr !important; }
          .main-content [style*="grid-template-columns: 240px"] { grid-template-columns: 1fr !important; }
          .main-content [style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }

          /* Tables scroll horizontally */
          .main-content table { display: block; overflow-x: auto; white-space: nowrap; }
          .table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }

          /* Reduce padding on cards */
          .main-content [style*="padding: 24px"] { padding: 16px !important; }
          .main-content [style*="padding: 28px"] { padding: 16px !important; }
          .main-content [style*="padding: 32px"] { padding: 16px !important; }
          .main-content [style*="padding:'24px'"] { padding: 16px !important; }
          .main-content [style*="padding:'28px'"] { padding: 16px !important; }
          .main-content [style*="padding:'32px'"] { padding: 16px !important; }

          /* Modals full screen */
          [style*="position:'fixed'"][style*="inset:0"] > div { 
            border-radius: 0 !important; 
            max-width: 100% !important;
            max-height: 100vh !important;
            overflow-y: auto !important;
          }

          /* Font sizes */
          .main-content h1 { font-size: 22px !important; }
          .main-content h2 { font-size: 18px !important; }

          /* Typography */
          h1 { font-size: 24px !important; }
          h2 { font-size: 20px !important; }

          /* Page headers — stack on mobile */
          .page-header { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }

          /* Grids — collapse to single column */
          .stat-grid,
          .stats-row,
          [style*="grid-template-columns: repeat(4"],
          [style*="grid-template-columns: repeat(3"],
          [style*="grid-template-columns: repeat(2"],
          [style*="gridTemplateColumns: 'repeat(4"],
          [style*="gridTemplateColumns: 'repeat(3"],
          [style*="gridTemplateColumns: 'repeat(2"] {
            grid-template-columns: 1fr 1fr !important;
          }

          /* Tables — make horizontally scrollable */
          .table-wrap { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
          table { min-width: 500px; }

          /* Cards with flex row — stack */
          .card-row { flex-direction: column !important; }

          /* Modal — full screen on mobile */
          .modal-inner {
            position: fixed !important;
            inset: 0 !important;
            border-radius: 0 !important;
            max-width: 100% !important;
            max-height: 100% !important;
            overflow-y: auto !important;
          }

          /* Buttons in flex row — wrap */
          .btn-row { flex-wrap: wrap !important; gap: 8px !important; }

          /* Hide low-priority columns in tables */
          .hide-mobile { display: none !important; }

          /* Filter chips — scroll horizontally */
          .filter-row { overflow-x: auto !important; flex-wrap: nowrap !important; padding-bottom: 4px; }
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          .desktop-sidebar { width: 180px !important; }
          .main-content { padding: 32px 32px !important; }
        }
      `}</style>
    </div>
  );
}